import { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, Calendar, Loader2, Sparkles, Plus, Save, X } from 'lucide-react';
import { getDocumentText } from '../utils/wordApi';
import { resolveContractContext } from '../utils/contractResolver';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface Obligation {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'active' | 'pending' | 'overdue';
  party: string;
  templateTypeId?: string;
}

interface ResponsibleUser {
  id: string;
  name: string;
  email: string;
}

interface ObligationType {
  id: string;
  name: string;
  description?: string | null;
}

interface CurrentContract {
  contractId: string;
  tenantId: string;
  templateId: string;
  templateTypeId: string;
  templateTypeName: string;
}

export function Obligation() {
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [currentContract, setCurrentContract] = useState<CurrentContract | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedObligation, setSelectedObligation] = useState<Obligation | null>(null);
  const [saving, setSaving] = useState(false);
  const [responsibleUsers, setResponsibleUsers] = useState<ResponsibleUser[]>([]);
  const [obligationTypes, setObligationTypes] = useState<ObligationType[]>([]);
  const [obligationFormData, setObligationFormData] = useState({
    startDate: '',
    endDate: '',
    obligationTypeId: '',
    personResponsibleId: '',
  });

  // Load current contract context (works with both WOPI and SharePoint/Auth modes)
  useEffect(() => {
    resolveContractContext().then((ctx) => {
      if (ctx) setCurrentContract(ctx as CurrentContract);
    }).catch((err) => console.error('Error loading current contract:', err));
  }, []);

  // Load obligations for current template
  useEffect(() => {
    const loadObligations = async () => {
      if (!currentContract) return;
      if (!currentContract.templateId) {
        // No template association — show empty state so the user can extract.
        setObligations([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/addin/obligations?templateId=${currentContract.templateId}&tenantId=${currentContract.tenantId}`,
          {
            headers: { 
              'ngrok-skip-browser-warning': 'true',
              'x-addin-token': API_TOKEN,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setObligations(data.obligations || []);
        }
      } catch (err) {
        console.error('Error loading obligations:', err);
        setError('Failed to load obligations');
      } finally {
        setLoading(false);
      }
    };

    loadObligations();
  }, [currentContract?.templateId, currentContract?.tenantId, currentContract?.contractId]);

  // Listen for global "analyze-all" event (fired by Home's Re-run analysis button)
  useEffect(() => {
    const handler = () => {
      if (!extracting && currentContract) {
        handleExtractFromDocument();
      }
    };
    window.addEventListener('ezylex:analyze-all', handler);
    return () => window.removeEventListener('ezylex:analyze-all', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContract, extracting]);

  // Load responsible users for obligation saving
  useEffect(() => {
    const loadUsers = async () => {
      if (!currentContract?.tenantId) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/addin/users-public?tenantId=${encodeURIComponent(currentContract.tenantId)}&limit=100`,
          {
            headers: {
              'x-addin-token': API_TOKEN,
              'ngrok-skip-browser-warning': 'true',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setResponsibleUsers(data.users || []);
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };

    loadUsers();
  }, [currentContract?.tenantId]);

  // Load obligation types for dropdown
  useEffect(() => {
    const loadObligationTypes = async () => {
      if (!currentContract?.tenantId) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/addin/obligation-types-public?tenantId=${encodeURIComponent(currentContract.tenantId)}`,
          {
            headers: {
              'x-addin-token': API_TOKEN,
              'ngrok-skip-browser-warning': 'true',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setObligationTypes(data.obligationTypes || []);
        }
      } catch (err) {
        console.error('Error loading obligation types:', err);
      }
    };

    loadObligationTypes();
  }, [currentContract?.tenantId]);

  // Ensure modal defaults are set even if lists load after modal open
  useEffect(() => {
    if (!showSaveModal) return;

    if (!obligationFormData.obligationTypeId && obligationTypes.length > 0) {
      setObligationFormData((prev) => ({
        ...prev,
        obligationTypeId: obligationTypes[0].id,
      }));
    }

    if (!obligationFormData.personResponsibleId && responsibleUsers.length > 0) {
      setObligationFormData((prev) => ({
        ...prev,
        personResponsibleId: responsibleUsers[0].id,
      }));
    }
  }, [showSaveModal, obligationFormData.obligationTypeId, obligationFormData.personResponsibleId, obligationTypes, responsibleUsers]);

  const handleExtractFromDocument = async () => {
    if (!currentContract) return;
    
    setExtracting(true);
    setError(null);
    
    try {
      const documentText = await getDocumentText();
      if (!documentText) {
        setError('Document is empty');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/addin/obligations/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          documentText,
          templateId: currentContract.templateId,
          tenantId: currentContract.tenantId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setObligations(data.obligations || []);
      } else {
        let detail = `HTTP ${response.status}`;
        try {
          const errData = await response.json();
          if (errData?.error) detail = errData.error;
        } catch {
          // ignore
        }
        setError(`Failed to extract obligations: ${detail}`);
      }
    } catch (err) {
      console.error('Error extracting obligations:', err);
      setError('An error occurred while extracting obligations');
    } finally {
      setExtracting(false);
    }
  };

  const handleInsertObligation = async (obligation: Obligation) => {
    try {
      await Word.run(async (context) => {
        // Get the current selection (cursor position)
        const selection = context.document.getSelection();
        
        // Insert paragraph break before obligation
        selection.insertParagraph('', Word.InsertLocation.before);
        
        // Insert obligation header
        const headerRange = selection.insertParagraph(
          `OBLIGATION: ${obligation.title}`,
          Word.InsertLocation.before
        );
        headerRange.font.bold = true;
        headerRange.font.size = 12;
        headerRange.font.color = '#1E40AF'; // Professional blue
        headerRange.spaceAfter = 6;
        
        // Insert description
        const descRange = selection.insertParagraph(
          `Description: ${obligation.description}`,
          Word.InsertLocation.after
        );
        descRange.font.size = 11;
        descRange.leftIndent = 20;
        descRange.spaceAfter = 3;
        
        // Insert due date
        const dueDateRange = selection.insertParagraph(
          `Due Date: ${obligation.dueDate}`,
          Word.InsertLocation.after
        );
        dueDateRange.font.size = 11;
        dueDateRange.leftIndent = 20;
        dueDateRange.spaceAfter = 3;
        
        // Insert responsible party
        const partyRange = selection.insertParagraph(
          `Responsible Party: ${obligation.party}`,
          Word.InsertLocation.after
        );
        partyRange.font.size = 11;
        partyRange.leftIndent = 20;
        partyRange.spaceAfter = 3;
        
        // Insert status with color coding
        const statusRange = selection.insertParagraph(
          `Status: ${obligation.status.toUpperCase()}`,
          Word.InsertLocation.after
        );
        statusRange.font.size = 11;
        statusRange.font.bold = true;
        statusRange.leftIndent = 20;
        statusRange.spaceAfter = 12;
        
        // Color code status
        if (obligation.status === 'active') {
          statusRange.font.color = '#059669'; // Green
        } else if (obligation.status === 'pending') {
          statusRange.font.color = '#D97706'; // Orange
        } else if (obligation.status === 'overdue') {
          statusRange.font.color = '#DC2626'; // Red
        }
        
        await context.sync();
        
        // Clear any previous error
        setError(null);
      });
    } catch (err) {
      console.error('Error inserting obligation:', err);
      setError('Failed to insert obligation into document');
    }
  };

  const handleOpenSaveModal = (obligation: Obligation) => {
    setSelectedObligation(obligation);
    setObligationFormData({
      startDate: '',
      endDate: '',
      obligationTypeId: obligationTypes[0]?.id || '',
      personResponsibleId: responsibleUsers[0]?.id || '',
    });
    setSaveStatus(null);
    setShowSaveModal(true);
  };

  const handleSaveObligationToClm = async () => {
    if (!currentContract || !selectedObligation) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/addin/obligations/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          title: selectedObligation.title,
          description: selectedObligation.description,
          status: selectedObligation.status,
          startDate: obligationFormData.startDate,
          endDate: obligationFormData.endDate,
          obligationTypeId: obligationFormData.obligationTypeId,
          personResponsibleId: obligationFormData.personResponsibleId,
          templateId: currentContract.templateId,
          tenantId: currentContract.tenantId,
          contractId: currentContract.contractId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSaveStatus({ success: true, message: data.message || 'Saved successfully' });
        setError(null);
        setShowSaveModal(false);
        setSelectedObligation(null);
      } else {
        let message = 'Failed to save obligation to CLM';
        try {
          const errData = await response.json();
          message = errData.error || message;
        } catch {
          // ignore
        }
        setSaveStatus({ success: false, message });
        setError(message);
      }
    } catch (err) {
      console.error('Error saving obligation:', err);
      const message = 'An error occurred while saving obligation';
      setSaveStatus({ success: false, message });
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const activeCount = obligations.filter(o => o.status === 'active').length;
  const pendingCount = obligations.filter(o => o.status === 'pending').length;
  const overdueCount = obligations.filter(o => o.status === 'overdue').length;

  return (
    <div className="p-3 h-full bg-gray-50 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-600 rounded-lg">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-gray-900">Obligations</h1>
              <p className="text-[10px] text-gray-500">
                {currentContract?.templateTypeName || 'Contract'}
              </p>
            </div>
          </div>
          <button
            onClick={handleExtractFromDocument}
            disabled={extracting || !currentContract}
            className="px-2.5 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-[11px] flex items-center gap-1.5"
          >
            {extracting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            Extract with AI
          </button>
        </div>

        {saveStatus && (
          <div
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-xs border ${
              saveStatus.success
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            <span>{saveStatus.message}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-500">Active</span>
              <CheckCircle className="w-3 h-3 text-green-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">{activeCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-500">Pending</span>
              <Clock className="w-3 h-3 text-blue-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-500">Overdue</span>
              <AlertCircle className="w-3 h-3 text-red-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">{overdueCount}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : obligations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">No obligations found</p>
            <p className="text-xs text-gray-500 mb-4">
              Click "Extract with AI" to analyze the document
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {obligations.map((obligation) => (
              <div
                key={obligation.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-2.5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-medium text-gray-900 text-xs truncate">{obligation.title}</h3>
                      <span
                        className={`px-1.5 py-0.5 text-[9px] rounded font-medium shrink-0 ${
                          obligation.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : obligation.status === 'pending'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {obligation.status.charAt(0).toUpperCase() + obligation.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-600 line-clamp-2 mb-1">{obligation.description}</p>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" />
                        {obligation.dueDate}
                      </span>
                      <span>•</span>
                      <span className="truncate">{obligation.party}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleInsertObligation(obligation)}
                      className="p-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                      title="Insert into document"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    {obligation.id.startsWith('extracted-') && (
                      <button
                        onClick={() => handleOpenSaveModal(obligation)}
                        className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        title="Save to CLM"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save to CLM Modal */}
        {showSaveModal && selectedObligation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">Save Obligation to CLM</h2>
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setSelectedObligation(null);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Obligation Title *
                  </label>
                  <input
                    type="text"
                    value={selectedObligation.title}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={selectedObligation.description}
                    readOnly
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={obligationFormData.startDate}
                      onChange={(e) => setObligationFormData({ ...obligationFormData, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={obligationFormData.endDate}
                      onChange={(e) => setObligationFormData({ ...obligationFormData, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Obligation Type *
                  </label>
                  <select
                    value={obligationFormData.obligationTypeId}
                    onChange={(e) => setObligationFormData({ ...obligationFormData, obligationTypeId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {obligationTypes.length === 0 ? (
                      <option value="">Loading obligation types...</option>
                    ) : (
                      obligationTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsible User *
                    </label>
                    <select
                      value={obligationFormData.personResponsibleId}
                      onChange={(e) => setObligationFormData({ ...obligationFormData, personResponsibleId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {responsibleUsers.length === 0 ? (
                        <option value="">Loading users...</option>
                      ) : (
                        responsibleUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <input
                      type="text"
                      value={selectedObligation.status.charAt(0).toUpperCase() + selectedObligation.status.slice(1)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Current template: {currentContract?.templateTypeName || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500">
                    This obligation will be saved to the current template.
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setSelectedObligation(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveObligationToClm}
                  disabled={
                    saving ||
                    !obligationFormData.startDate ||
                    !obligationFormData.endDate ||
                    !obligationFormData.obligationTypeId ||
                    !obligationFormData.personResponsibleId
                  }
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save to CLM
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
